import React from 'react';
import { Flex, Text } from '@amzn/storm-ui';
import styled from 'styled-components';
import {
  accountSelectionListId,
  IA2AccountDropdownSearchInput,
  AccountTypeLabelContainer,
  AccountTypeLabel,
  IA2AccountListItem,
} from './styles';
import { useAccountDropdownSearch } from './useAccountDropdownSearch';
import { IA2AccountDropdownTypeSection } from './IA2AccountDropdownTypeSection';
import { AccountFlatList } from './AccountFlatList';
import { useAccountData } from './useAccountData';
import { IA2AccountDropdownInfoCard } from './IA2AccountDropdownInfoCard';
import {
  AdvertisingAccountLink,
  AdvertisingAccountContainer,
} from './IA2AdvertisingAccountItem';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { IA2AccountDropdownFooterLinks } from './IA2AccountDropdownFooterLinks';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getAccountTypeListLabel } from './AccountTypeLocalizationKeyMapping';
import { NoAccountsMessage } from './NoAccountsMessage';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import {
  findSelectedAccountSwitcherAccount,
  buildAllAccountsUrl,
} from './accountHelper';
import { ErrorLoadingAccountsMessage } from 'src/features/header/components/AccountDropdown/ErrorLoadingAccountsMessage';
import { getTaktAttributes, logWithTakt } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

const SEARCH_DISPLAY_COUNT = 10;

const AccountSwitcherInfoCardContainer = styled(Flex).attrs({
  direction: 'column',
})`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.base.default};
`;

const AccountListContainer = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 0;
  border-block: ${({ theme }) => theme.border.subtle.default};
`;

const AccountDropdownWithSearchContainer = styled(Flex).attrs({
  direction: 'column',
  alignItems: 'inherit',
})`
  max-height: 80vh;
`;

const AllAccountsMessageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  display: flex;
  justify-content: center;
`;

type IA2AccountDropdownProps = {
  advertisingAccountsDataTaktId?: string;
};

export const IA2AccountDropdown: React.VFC<IA2AccountDropdownProps> = ({
  advertisingAccountsDataTaktId,
}) => {
  const {
    currentAccountName,
    accounts,
    currentEntityId,
    currentMarketplaceId,
    accountCounter,
    loadingStatus,
    isPrecomputed,
    totalCount,
    nextToken,
  } = useAccountDropdownContext();
  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM,
  );
  const isT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimNavMigrationEnabled = isT1Enabled || isT2Enabled;
  const pageScope = chromeConfig.pageHeader?.pageScope;
  const supportedPageScopes = chromeConfig.pageHeader?.supportedPageScopes;
  const isSwitchable =
    Array.isArray(supportedPageScopes) && supportedPageScopes.length > 1;
  const isCurrentScopeAllAccounts = pageScope === 'ALL_ACCOUNTS';
  const isAllAccountsModeOnly = isCurrentScopeAllAccounts && !isSwitchable;
  const isFailedLoadingAccountData = loadingStatus === 'error';
  const listContainerRef = React.useRef<HTMLDivElement>(null);

  // Both data hooks are called unconditionally (rules of hooks); the weblab flag
  // selects which drives rendering. Control = legacy local-only search + paginated
  // type sections; treatment = unified flat list + dual-mode useAccountData.
  const { onSearch, accountTypeList, onClear, searchValue } =
    useAccountDropdownSearch({
      accounts,
    });
  const dataSource = useAccountData({
    isPrecomputed: isPrecomputed === true,
    seedAccounts: accounts,
    seedTotalCount: totalCount ?? 0,
    seedNextToken: nextToken ?? null,
    seedFailed: isFailedLoadingAccountData,
    currentMarketplaceId,
  });

  // The dropdown lazily mounts on open (Popover renders children only when
  // open), so mount == open. Emit the open funnel event plus the open→first-
  // render latency (a useEffect fires after the browser paints) once per open,
  // treatment only. accountCount is the whole-inventory total from navbar-init.
  const openMountTime = React.useRef<number>(performance.now());
  React.useEffect(() => {
    if (!isTrimNavMigrationEnabled) {
      return;
    }
    logWithTakt('ccx-account-switcher-open', {
      isPrecomputed: isPrecomputed === true,
      accountCount: totalCount ?? 0,
    });
    logWithTakt('ccx-account-switcher-latency', {
      isPrecomputed: isPrecomputed === true,
      accountCount: totalCount ?? 0,
      latencyMs: Math.round(performance.now() - openMountTime.current),
    });
    // Mount-only: fire once per dropdown open.
    // Intentionally excludes the values read above so this fires only on open,
    // not on every change to them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show search only when it's useful: more than a page-full of accounts, or
  // more pages exist server-side (nextToken != null). A small/single-account
  // entity (e.g. a DSP entity with one account, nextToken null) shows no search.
  // Control keeps its existing >10 gate; treatment adds the has-more-pages case.
  const hasMorePages = isTrimNavMigrationEnabled && nextToken != null;
  const isSearchInputVisible =
    accountCounter > SEARCH_DISPLAY_COUNT || hasMorePages;

  const accountSearchPlaceholder = bundle.getMessage(
    'account-dropdown-placeholder',
  );

  if (isAllAccountsModeOnly) {
    return (
      <AllAccountsMessageContainer>
        <Text>{bundle.getMessage('global-accounts-pill-message')}</Text>
      </AllAccountsMessageContainer>
    );
  }
  // Do not highlight any individual account when the user is in All Accounts
  // scope — no single entity is "active" in that context, so passing an
  // entityId to the list would incorrectly mark one account as selected.
  const selectedAccount = isCurrentScopeAllAccounts
    ? undefined
    : findSelectedAccountSwitcherAccount(
        currentEntityId,
        accounts.map((accountGroup) => accountGroup.accounts).flat(),
      );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTrimNavMigrationEnabled) {
      dataSource.search(e.target.value);
    } else {
      onSearch(e);
    }
  };

  const handleSearchClear = () => {
    if (isTrimNavMigrationEnabled) {
      dataSource.clearSearch();
    } else {
      onClear();
    }
  };

  return (
    <AccountDropdownWithSearchContainer>
      <AccountSwitcherInfoCardContainer direction="column">
        {!isCurrentScopeAllAccounts && !!currentAccountName && (
          <IA2AccountDropdownInfoCard />
        )}
        {isSearchInputVisible && (
          <IA2AccountDropdownSearchInput
            id="account-switcher-search"
            placeholder={accountSearchPlaceholder}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            fullWidth={true}
            label={accountSearchPlaceholder}
            {...(!isTrimNavMigrationEnabled && { value: searchValue })}
            {...getTaktAttributes(
              'ccx-account-switcher-search',
              chromeComponents.HEADER,
            )}
          />
        )}
      </AccountSwitcherInfoCardContainer>
      <AccountListContainer
        id={accountSelectionListId}
        ref={listContainerRef}
        {...(!!advertisingAccountsDataTaktId &&
          getTaktAttributes(
            advertisingAccountsDataTaktId,
            chromeComponents.HEADER,
          ))}
      >
        {isSwitchable && (
          <Flex direction="column" alignItems="normal">
            <AccountTypeLabelContainer>
              <AccountTypeLabel weight="bold" withMargin>
                {bundle.getMessage('account-switcher-all-accounts-label')}
              </AccountTypeLabel>
            </AccountTypeLabelContainer>
            <IA2AccountListItem isSelected={isCurrentScopeAllAccounts}>
              {isCurrentScopeAllAccounts ? (
                <AdvertisingAccountContainer>
                  <Text>
                    {bundle.getMessage('account-switcher-all-accounts-label')}
                  </Text>
                </AdvertisingAccountContainer>
              ) : (
                <AdvertisingAccountLink
                  data-ccx-e2e-id="aac-all-accounts-link"
                  href={buildAllAccountsUrl()}
                  draggable="false"
                  {...getTaktAttributes(
                    'aac-all-accounts-link',
                    chromeComponents.HEADER,
                  )}
                >
                  <Text>
                    {bundle.getMessage('account-switcher-all-accounts-label')}
                  </Text>
                </AdvertisingAccountLink>
              )}
            </IA2AccountListItem>
          </Flex>
        )}
        {isTrimNavMigrationEnabled ? (
          <AccountFlatList
            dataSource={dataSource}
            selectedEntityId={selectedAccount?.entityId}
            scrollRootRef={listContainerRef}
          />
        ) : (
          <>
            {isFailedLoadingAccountData && <ErrorLoadingAccountsMessage />}
            {!isFailedLoadingAccountData && accountTypeList.length === 0 && (
              <NoAccountsMessage />
            )}
            {!isFailedLoadingAccountData &&
              accountTypeList.length > 0 &&
              accountTypeList.map((accountTypeItem, index) => (
                <IA2AccountDropdownTypeSection
                  key={`${accountTypeItem.label ?? ''}-${index}`}
                  accounts={accountTypeItem.accounts}
                  label={getAccountTypeListLabel(
                    accountTypeItem.accountType,
                    bundle,
                  )}
                  type={accountTypeItem.accountType}
                  selectedEntityId={selectedAccount?.entityId}
                  isLastAccountType={index === accountTypeList.length - 1}
                />
              ))}
          </>
        )}
      </AccountListContainer>
      <IA2AccountDropdownFooterLinks />
    </AccountDropdownWithSearchContainer>
  );
};
